import { get, post, patch } from './api-client';
import axios from 'axios';
import { Product, ApiResponse } from '../types';
import { handleApiError } from '../utils/error-utils';
import API_CONFIG from '../config/api';

// Interface for paginated responses
interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page?: number;
  limit?: number;
  hasMore?: boolean;
}

const parseProductsResponse = (response: any): PaginatedResponse<Product> | null => {
  if (Array.isArray(response)) {
    return {
      items: response,
      total: response.length,
    };
  }

  if (response && typeof response === 'object' && 'items' in response && 'total' in response) {
    return response as PaginatedResponse<Product>;
  }

  return null;
};

// Interface for product creation
export interface CreateProductRequest {
  sku: string;
  name: string;
  description: string;
  price: number;
  is_active: boolean;
  manual: string;
  cisp1_commission: number;
  reseller_commission: number;
  use_acer_empresas_price_rule?: boolean;
  acer_empresas_net_price?: number;
  category: string;
  images: Array<{
    image_url: string;
    alt_text: string;
    is_primary: boolean;
    display_order: number;
  }>;
  specifications?: Record<string, any>;
}

// Interface for product update
export interface UpdateProductRequest {
  id: string;
  sku: string;
  name: string;
  description: string;
  price: number;
  is_active: boolean;
  manual: string;
  cisp1_commission: number;
  reseller_commission: number;
  use_acer_empresas_price_rule?: boolean;
  acer_empresas_net_price?: number;
  category: string;
  images: Array<{
    id?: string;
    product_id?: string;
    image_url: string;
    alt_text: string;
    is_primary: boolean;
    display_order: number;
  }>;
  specifications?: Record<string, any>;
}

// Interface for product creation/update response
export interface ProductResponse {
  id: string;
}

// Get all active products
export const getActiveProducts = async (
  page = 1,
  limit = 10
): Promise<PaginatedResponse<Product> | null> => {
  try {
    console.log(`Fetching active products, page ${page}`);
    console.log(`Fetching products from: /products?skip=${(page - 1) * limit}&limit=${limit}`);

    // The api-client's get function already returns response.data
    const response = await get<any>(`/products/?skip=${(page - 1) * limit}&limit=${limit}`);

    console.log('Raw API response:', response);

    // If response is directly an array of products (based on your API response example)
    if (Array.isArray(response)) {
      console.log('Response is an array with', response.length, 'items');
      const loadedItems = (page - 1) * limit + response.length;
      return {
        items: response,
        total: response.length === limit ? loadedItems + 1 : loadedItems,
        page,
        limit,
        hasMore: response.length === limit,
      };
    }

    // Handle case where response has items and total
    if (response && typeof response === 'object' && 'items' in response && 'total' in response) {
      console.log('Response is a paginated object');
      return {
        ...(response as PaginatedResponse<Product>),
        hasMore: page * limit < (response as PaginatedResponse<Product>).total,
      };
    }

    console.log('Unknown response format');
    return null;
  } catch (error) {
    console.error('Error fetching products:', error);
    handleApiError(error);
    return null;
  }
};

// Get a specific product by ID
export const getProductById = async (id: string): Promise<Product | null> => {
  try {
    // The api-client's get function already returns response.data
    const product = await get<Product>(`/admin/products/${id}/details`);

    console.log('Raw product API response:', product);

    // The product should be returned directly
    return product;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

// Search products with a query
export const searchProducts = async (
  query: string,
  page = 1,
  limit = 10
): Promise<PaginatedResponse<Product> | null> => {
  try {
    // The api-client's get function already returns response.data
    const response = await get<any>(
      `/products/?query=${encodeURIComponent(query)}&skip=${(page - 1) * limit}&limit=${limit}`
    );

    console.log('Raw search API response:', response);

    // If response is directly an array of products
    if (Array.isArray(response)) {
      console.log('Search response is an array with', response.length, 'items');
      const loadedItems = (page - 1) * limit + response.length;
      return {
        items: response,
        total: response.length === limit ? loadedItems + 1 : loadedItems,
        page,
        limit,
        hasMore: response.length === limit,
      };
    }

    // Handle case where response has items and total
    if (response && typeof response === 'object' && 'items' in response && 'total' in response) {
      console.log('Search response has items and total properties');
      return {
        ...(response as PaginatedResponse<Product>),
        hasMore: page * limit < (response as PaginatedResponse<Product>).total,
      };
    }

    console.log('Search response format not recognized, returning empty result');
    // If we got here, format is unknown, return empty
    return {
      items: [],
      total: 0,
    };
  } catch (error) {
    console.error('Error searching products:', error);
    handleApiError(error);
    return {
      items: [],
      total: 0,
    };
  }
};

// Get products with customer-specific pricing for quotes
export const getProductsForQuote = async (
  customerId: string,
  page = 1,
  limit = 10,
  query?: string
): Promise<PaginatedResponse<Product> | null> => {
  try {
    const params = new URLSearchParams({
      customer_id: customerId,
      skip: String((page - 1) * limit),
      limit: String(limit),
    });

    if (query?.trim()) {
      params.set('query', query.trim());
    }

    const response = await get<any>(`/products/for-quote?${params.toString()}`);

    console.log('Raw for-quote products API response:', response);

    const parsedResponse = parseProductsResponse(response);
    if (parsedResponse) {
      return parsedResponse;
    }

    console.log('For-quote response format not recognized, returning empty result');
    return {
      items: [],
      total: 0,
    };
  } catch (error) {
    console.error('Error fetching products for quote:', error);
    handleApiError(error);
    return {
      items: [],
      total: 0,
    };
  }
};

// Create a new product
export const createProduct = async (
  productData: CreateProductRequest
): Promise<ProductResponse | null> => {
  try {
    console.log('Creating product with data:', productData);

    // Use the post method to create a new product
    const response = await post<ProductResponse, CreateProductRequest>(
      '/admin/products',
      productData
    );

    console.log('Product creation response:', response);
    return response;
  } catch (error) {
    console.error('Error creating product:', error);
    handleApiError(error);
    throw error; // Re-throw to let the component handle it
  }
};

// Update an existing product
export const updateProduct = async (
  productId: string,
  productData: UpdateProductRequest
): Promise<ProductResponse | null> => {
  try {
    console.log('Updating product with data:', productData);

    // Use the patch method to update the product
    const response = await patch<ProductResponse, UpdateProductRequest>(
      `/admin/products/${productId}/complete/`,
      productData
    );

    console.log('Product update response:', response);
    return response;
  } catch (error) {
    console.error('Error updating product:', error);
    handleApiError(error);
    throw error; // Re-throw to let the component handle it
  }
};
